import edaLogo from "@/assets/logos/eda.png";
import dostLogo from "@/assets/logos/dost.png";
import taxiLogo from "@/assets/logos/taxi.png";

/**
 * Сервисы и тарифы демо. Только то, по чему у kef-compare есть данные:
 * пять срезов (коды тарифов — из kef-compare/app/config.py). Купер, классы
 * Такси кроме эконома, Мото и Грузовой — в источнике отсутствуют, поэтому
 * их здесь нет (решение 07.09).
 */
export type ServiceKey = "eda" | "dost" | "taxi";
export type TariffKey = "foot" | "auto" | "econom";

export interface Service {
  key: ServiceKey;
  name: string;
  short: string;
  /** Родительный падеж — для подписи «больше, чем у …». */
  gen: string;
  color: string;
  logo: string;
  tariffs: TariffKey[];
}

export const SERVICES: Record<ServiceKey, Service> = {
  eda: {
    key: "eda",
    name: "Яндекс Еда",
    short: "Еда",
    gen: "Еды",
    color: "#F0A122",
    logo: edaLogo,
    tariffs: ["foot", "auto"],
  },
  dost: {
    key: "dost",
    name: "Яндекс Доставка",
    short: "Доставка",
    gen: "Доставки",
    color: "#FF3D6E",
    logo: dostLogo,
    tariffs: ["foot", "auto"],
  },
  taxi: {
    key: "taxi",
    name: "Яндекс Такси",
    short: "Такси",
    gen: "Такси",
    color: "#29B6F6",
    logo: taxiLogo,
    tariffs: ["econom"],
  },
};

export const SERVICE_ORDER: ServiceKey[] = ["eda", "dost", "taxi"];

export const TARIFF_LABEL: Record<TariffKey, string> = {
  foot: "Пеший",
  auto: "Авто",
  econom: "Эконом",
};

/** (сервис, тариф) → код тарифа kef-compare / ассистента. */
export const KEFC_TARIFF: Record<ServiceKey, Partial<Record<TariffKey, number>>> = {
  dost: { foot: 1, auto: 2 },
  taxi: { econom: 5 },
  eda: { foot: 6, auto: 7 },
};

export interface Pick {
  k: ServiceKey;
  t: TariffKey;
}

export const pickId = (p: Pick) => `${p.k}:${p.t}`;
export const pickCode = (p: Pick): number => KEFC_TARIFF[p.k][p.t] as number;
export const pickLabel = (p: Pick) =>
  `${SERVICES[p.k].short} · ${TARIFF_LABEL[p.t]}`;

export const ALL_PICKS: Pick[] = SERVICE_ORDER.flatMap((k) =>
  SERVICES[k].tariffs.map((t) => ({ k, t }))
);

export const DEFAULT_PICKS: Pick[] = [
  { k: "eda", t: "auto" },
  { k: "dost", t: "auto" },
  { k: "taxi", t: "econom" },
  { k: "dost", t: "foot" },
];

export const MAX_PICKS = 4;

/** Наборы для анкеты консультанта: с машиной / без. */
export const PICKS_CAR: Pick[] = [
  { k: "eda", t: "auto" },
  { k: "dost", t: "auto" },
  { k: "taxi", t: "econom" },
];
export const PICKS_FOOT: Pick[] = [
  { k: "eda", t: "foot" },
  { k: "dost", t: "foot" },
];

/**
 * Города — только три, по которым у kef-compare есть скоуп. Ключи совпадают
 * с ключами kef-compare и ассистента (`msk`, `spb`, `samara`).
 */
export type CityKey = "msk" | "spb" | "samara";

export interface City {
  key: CityKey;
  name: string;
  /** Предложный падеж — «в Москве». */
  loc: string;
  tz: number;
  center: { lat: number; lon: number };
}

export const CITIES: Record<CityKey, City> = {
  msk: {
    key: "msk",
    name: "Москва",
    loc: "Москве",
    tz: 3,
    center: { lat: 55.7558, lon: 37.6173 },
  },
  spb: {
    key: "spb",
    name: "Санкт-Петербург",
    loc: "Санкт-Петербурге",
    tz: 3,
    center: { lat: 59.9343, lon: 30.3351 },
  },
  samara: {
    key: "samara",
    name: "Самара",
    loc: "Самаре",
    tz: 4,
    center: { lat: 53.1959, lon: 50.1002 },
  },
};

export const CITY_ORDER: CityKey[] = ["msk", "spb", "samara"];

/** Москва — по умолчанию: в Самаре нет среза «Еда · Авто». */
export const DEFAULT_CITY: CityKey = "msk";

/** Ближайший из трёх городов к точке — для геопозиции. */
export function nearestCity(lat: number, lon: number): CityKey {
  let best: CityKey = DEFAULT_CITY;
  let bestD = Infinity;
  CITY_ORDER.forEach((key) => {
    const c = CITIES[key].center;
    const d = (c.lat - lat) ** 2 + ((c.lon - lon) * Math.cos((lat * Math.PI) / 180)) ** 2;
    if (d < bestD) {
      bestD = d;
      best = key;
    }
  });
  return best;
}

/** Линии одного сервиса разводятся по светлоте — как в прототипе (shade). */
export function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) => Math.max(0, Math.min(255, v + amount));
  const r = ch(n >> 16);
  const g = ch((n >> 8) & 255);
  const b = ch(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
